// Side-effect import: initialises Sentry-compatible monitoring (Sentry,
// GlitchTip, etc.) if VITE_SENTRY_DSN is set. MUST run before any React
// render so global error handlers are installed first.
import "./lib/monitoring";

import { RouterProvider, createRouter } from "@tanstack/react-router";
import { reportToMonitoring } from "@template/shared";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { routeTree } from "./routeTree.gen";
import { NotFound, RouteErrorFallback } from "./shared/components/not-found";
import "./globals.css";

// Global error funnels — anything not caught by a React ErrorBoundary or
// tRPC handler ends up here. `reportToMonitoring` is a no-op stub by default;
// replace the export in `@template/shared` to wire Sentry (or equivalent).
window.addEventListener("error", (event) => {
  reportToMonitoring(event.error ?? new Error(event.message), {
    type: "windowError",
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
  });
});

window.addEventListener("unhandledrejection", (event) => {
  const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
  reportToMonitoring(error, { type: "unhandledRejection" });
});

const router = createRouter({
  routeTree,
  defaultNotFoundComponent: NotFound,
  defaultErrorComponent: RouteErrorFallback,
  // Scroll na začátek při navigaci (a obnovení pozice při zpět). Bez toho si
  // SPA drží scroll z předchozí stránky — např. odkaz z patičky na
  // /obchodni-podminky otevřel stránku odscrollovanou dole.
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root");
if (rootElement) {
  createRoot(rootElement).render(
    <StrictMode>
      <RouterProvider router={router} />
    </StrictMode>,
  );
}
